// Paylaşılan enum'lar — API (Prisma) ve istemciler aynı değerleri kullanır.

/** Ürünün kısa bağlantı alan adı (SMS, QR etiket, NFC kart, CNAME). Sunucu canlıda PUBLIC_SHORT_URL'yi kullanır. */
export const PRODUCT_DOMAIN = 'serota.tr';

export const TENANT_TYPES = ['SERVICE_COMPANY', 'INSTITUTION', 'PLATFORM'] as const;
export type TenantType = (typeof TENANT_TYPES)[number];

export const MODULES = ['SCHOOL', 'FINANCE', 'NFC', 'AI_OPTIMIZATION', 'WHITE_LABEL', 'API'] as const;
export type Module = (typeof MODULES)[number];

export const MODULE_LABELS: Record<Module, string> = {
  SCHOOL: 'Okul',
  FINANCE: 'Finans',
  NFC: 'NFC',
  AI_OPTIMIZATION: 'AI Optimizasyon',
  WHITE_LABEL: 'Beyaz etiket',
  API: 'API',
};

/** Bayrak kapalıyken gizlenenler (01-product-architecture.md). */
export const MODULE_HIDES: Record<Module, string> = {
  SCHOOL: 'Çocuk, veli, rehber, teslim kodu, teslim alacak kişiler, öğrenci listesi',
  FINANCE: 'Kazanç (Driver), Finans menüsü, hakediş, fatura taslağı, ödeme (Passenger)',
  NFC: 'NFC kart bilgisi, NFC cihazları, kart tasarımı',
  AI_OPTIMIZATION: 'Optimizasyon, senaryolar, öneriler, talep ısı haritası',
  WHITE_LABEL: 'Branding ayarı, özel alan adı',
  API: 'API anahtarları, webhook logları',
};

export const DIRECTIONS = ['OUTBOUND', 'INBOUND'] as const;
export type Direction = (typeof DIRECTIONS)[number];
export const DIRECTION_LABELS: Record<Direction, string> = { OUTBOUND: 'Gidiş', INBOUND: 'Dönüş' };

export const TRIP_STATUSES = ['SCHEDULED', 'READY', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
export type TripStatus = (typeof TRIP_STATUSES)[number];

export const BOARDING_TYPES = ['BOARDED', 'NOT_BOARDED', 'NO_SHOW', 'ALIGHTED', 'DELIVERED'] as const;
export type BoardingType = (typeof BOARDING_TYPES)[number];

export const BOARDING_METHODS = ['QR', 'PIN', 'NFC', 'GUIDE', 'PICKUP_CODE', 'MANUAL'] as const;
export type BoardingMethod = (typeof BOARDING_METHODS)[number];
export const BOARDING_METHOD_LABELS: Record<BoardingMethod, string> = {
  QR: 'QR ile doğrulandı',
  PIN: 'PIN ile doğrulandı',
  NFC: 'NFC kart ile doğrulandı',
  GUIDE: 'Rehber onayı',
  PICKUP_CODE: 'Teslim kodu ile',
  MANUAL: 'Şoför onayı',
};

export const PASSENGER_KINDS = ['EMPLOYEE', 'STUDENT', 'GENERAL'] as const;
export type PassengerKind = (typeof PASSENGER_KINDS)[number];

export const INCIDENT_TYPES = [
  'TRAFFIC',
  'ACCIDENT',
  'BREAKDOWN',
  'ROAD_CLOSED',
  'PASSENGER',
  'MEDICAL',
  'OTHER',
] as const;
export type IncidentType = (typeof INCIDENT_TYPES)[number];
export const INCIDENT_LABELS: Record<IncidentType, string> = {
  TRAFFIC: 'Trafik',
  ACCIDENT: 'Kaza',
  BREAKDOWN: 'Araç arızası',
  ROAD_CLOSED: 'Yol kapalı',
  PASSENGER: 'Yolcu sorunu',
  MEDICAL: 'Sağlık durumu',
  OTHER: 'Diğer',
};

export const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const ALERT_TYPES = [
  'DELAY',
  'GPS_LOST',
  'BREAKDOWN',
  'INCIDENT',
  'VEHICLE_CHECK',
  'DOC_EXPIRING',
  'DOC_UPLOADED',
  'LOW_OCCUPANCY',
  'MAINTENANCE_DUE',
] as const;
export type AlertType = (typeof ALERT_TYPES)[number];

export const COMPLAINT_CATEGORIES = [
  'LATE',
  'SKIPPED_STOP',
  'DANGEROUS_DRIVING',
  'DRIVER_BEHAVIOR',
  'CLEANLINESS',
  'AIR_CONDITIONING',
  'OVERCROWDED',
  'ROUTE',
  'OTHER',
] as const;
export type ComplaintCategory = (typeof COMPLAINT_CATEGORIES)[number];
export const COMPLAINT_CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  LATE: 'Geç kaldı',
  SKIPPED_STOP: 'Durakta durmadı',
  DANGEROUS_DRIVING: 'Tehlikeli sürüş',
  DRIVER_BEHAVIOR: 'Şoför davranışı',
  CLEANLINESS: 'Araç temizliği',
  AIR_CONDITIONING: 'Klima',
  OVERCROWDED: 'Fazla yolcu',
  ROUTE: 'Rota',
  OTHER: 'Diğer',
};

export const COMPLAINT_STATUSES = ['NEW', 'REVIEWING', 'WAITING_DRIVER', 'RESOLVED', 'REJECTED'] as const;
export type ComplaintStatus = (typeof COMPLAINT_STATUSES)[number];
export const COMPLAINT_STATUS_LABELS: Record<ComplaintStatus, string> = {
  NEW: 'Yeni',
  REVIEWING: 'İnceleniyor',
  WAITING_DRIVER: 'Şoförden açıklama',
  RESOLVED: 'Çözüldü',
  REJECTED: 'Reddedildi',
};

export const REQUEST_TYPES = [
  'ADDRESS_CHANGE',
  'ROUTE_REQUEST',
  'TEMP_STOP',
  'DIFFERENT_LINE',
  'NEW_SERVICE',
  'TEMP_ADDRESS',
  'JOIN_SERVICE',
] as const;
export type RequestType = (typeof REQUEST_TYPES)[number];
export const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  ADDRESS_CHANGE: 'Adres değişikliği',
  ROUTE_REQUEST: 'Rota talebi',
  TEMP_STOP: 'Geçici durak',
  DIFFERENT_LINE: 'Farklı hat',
  NEW_SERVICE: 'Yeni servis',
  TEMP_ADDRESS: 'Geçici adres',
  JOIN_SERVICE: 'Servise katılım',
};

export const REQUEST_STATUSES = ['NEW', 'REVIEWING', 'INSTITUTION_APPROVAL', 'APPROVED', 'REJECTED'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];
export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  NEW: 'Yeni',
  REVIEWING: 'İnceleniyor',
  INSTITUTION_APPROVAL: 'Kurum onayında',
  APPROVED: 'Onaylandı',
  REJECTED: 'Reddedildi',
};

export const DOCUMENT_TYPES = [
  'LICENSE',
  'SRC',
  'PSYCHOTECHNIC',
  'ID_CARD',
  'PHOTO',
  'CRIMINAL_RECORD',
  'REGISTRATION',
  'INSPECTION',
  'INSURANCE',
  'CASCO',
  'ROUTE_PERMIT',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  LICENSE: 'Ehliyet',
  SRC: 'SRC',
  PSYCHOTECHNIC: 'Psikoteknik',
  ID_CARD: 'Kimlik',
  PHOTO: 'Fotoğraf',
  CRIMINAL_RECORD: 'Adli sicil kaydı',
  REGISTRATION: 'Ruhsat',
  INSPECTION: 'Muayene',
  INSURANCE: 'Trafik sigortası',
  CASCO: 'Kasko',
  ROUTE_PERMIT: 'Güzergâh izin belgesi',
};
export const DRIVER_DOCUMENTS: DocumentType[] = [
  'LICENSE',
  'SRC',
  'PSYCHOTECHNIC',
  'ID_CARD',
  'PHOTO',
  'CRIMINAL_RECORD',
];
export const VEHICLE_DOCUMENTS: DocumentType[] = ['REGISTRATION', 'INSPECTION', 'INSURANCE', 'CASCO', 'ROUTE_PERMIT'];

export const DOCUMENT_STATUSES = ['PENDING', 'VALID', 'EXPIRING', 'EXPIRED', 'REJECTED'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const NOTIFICATION_TYPES = [
  'TRIP_STARTED',
  'APPROACHING',
  'ARRIVED',
  'DELAY',
  'VEHICLE_CHANGED',
  'DRIVER_CHANGED',
  'CHILD_BOARDED',
  'CHILD_ARRIVED',
  'CHILD_DELIVERED',
  'CHILD_NOT_BOARDED',
  'ANNOUNCEMENT',
  'ROUTE_UPDATED',
  'REQUEST_UPDATED',
  'COMPLAINT_UPDATED',
  'MATCH_APPROVED',
  'TRIP_CANCELLED',
  // panel kullanıcıları (teklif akışı; uygulama içi)
  'PROPOSAL_SHARED',
  'PROPOSAL_COMMENT',
  'PROPOSAL_DECIDED',
  'PROPOSAL_WITHDRAWN',
  // finans (panel kullanıcıları; uygulama içi)
  'INVOICE_ISSUED',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
/** Çocuk güvenliği bildirimleri kapatılamaz, yalnızca sessize alınır. */
export const SAFETY_NOTIFICATIONS: NotificationType[] = [
  'CHILD_BOARDED',
  'CHILD_ARRIVED',
  'CHILD_DELIVERED',
  'CHILD_NOT_BOARDED',
];

export const PLAN_KEYS = ['STARTER', 'GROWTH', 'BUSINESS', 'ENTERPRISE'] as const;
export type PlanKey = (typeof PLAN_KEYS)[number];

export const APP_ROLES = ['PASSENGER', 'GUARDIAN', 'DRIVER', 'GUIDE'] as const;
export type AppRole = (typeof APP_ROLES)[number];

export const ANNOUNCEMENT_AUDIENCES = ['ALL', 'INSTITUTION', 'ROUTE', 'VEHICLE', 'DRIVERS', 'PARENTS'] as const;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];
export const ANNOUNCEMENT_CHANNELS = ['PUSH', 'SMS', 'IN_APP'] as const;
export type AnnouncementChannel = (typeof ANNOUNCEMENT_CHANNELS)[number];

export const INTERVENTION_TYPES = [
  'CHANGE_VEHICLE',
  'CHANGE_DRIVER',
  'CHANGE_GUIDE',
  'UPDATE_ROUTE',
  'CANCEL',
  'NOTIFY',
] as const;
export type InterventionType = (typeof INTERVENTION_TYPES)[number];

/**
 * Rota adındaki iki uç arasındaki ayraç: ok (→, ->, >) ya da boşluklu tire (" - ", " – ", " — ").
 * Boşluksuz tire yer adının parçası sayılır ("Kavaklı-Silivri" tek uçtur).
 */
const ROUTE_NAME_SEPARATOR = /(\s*(?:→|->|>)\s*|\s+[-–—]\s+)/;

/**
 * Güzergâh adı yöne göre: "Beylikdüzü → Maslak" dönüşte "Maslak → Beylikdüzü"; kullanıcının ayracı korunur
 * ("Büyükçekmece - Silivri" → "Silivri - Büyükçekmece"). İki uçlu değilse ad olduğu gibi kalır.
 */
export function directedRouteName(name: string, direction: Direction): string {
  if (direction !== 'INBOUND') return name;
  const parts = name.split(ROUTE_NAME_SEPARATOR);
  if (parts.length !== 3) return name;
  const [from, sep, to] = parts as [string, string, string];
  if (!from.trim() || !to.trim()) return name;
  return `${to.trim()}${sep}${from.trim()}`;
}
